import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore, type AuthUser } from '../store/authStore';

/**
 * RoleGuard — cycle 1-18 항목3. 라우트 레벨 role 가드.
 *
 * 배경: 기존엔 /admin-console·/system-admin/* 가 단일 <AuthGuard>(인증만) 하위에 있어 STAFF 도
 * 경로에 진입 가능했고 페이지 내부에서만 거부했다. RoleGuard 가 라우트 element 를 감싸 진입 자체를
 * 차단(UX). AuthGuard 를 확장하지 않고 별도 컴포넌트로 둬 단일 책임(AuthGuard=인증, RoleGuard=인가)
 * 을 보존한다.
 *
 * 경계 선언 (중요): RoleGuard 는 **UX 가드일 뿐 보안 경계가 아니다**. 권위 있는 차단은 백엔드
 * requireAuth(requiredRoles) 가 담당한다. role 강등이 access token TTL window 내 발생하면 그동안
 * 프론트는 store 의 구 role 을 신뢰하나, 백엔드가 최종 차단하므로 데이터는 안전하다.
 *
 * role SSOT: authStore.user.role (= AuthGuard 의 /users/me probe 캐시, 서버가 SSOT).
 * 미인가 → /dashboard redirect (403 전용 페이지 미신설 — 존재 노출 최소).
 *
 * AuthGuard 하위에서만 사용 — authStatus 가 이미 결정(authed) 됐다고 가정하되, probe 진행 중
 * (pending) 이면 판단 보류(렌더 안 함)해 깜빡임/오판을 방지한다.
 */
export function RoleGuard({
  allow,
  children,
}: {
  allow: ReadonlyArray<AuthUser['role']>;
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const authStatus = useAuthStore((s) => s.authStatus);

  // probe 진행 중 — AuthGuard 가 곧 authed/anon 으로 확정. 그 전엔 판단 보류.
  if (authStatus === 'pending') return null;

  if (!user || !allow.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
